import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Spinner,
} from "reactstrap";
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import moment from "moment";
import Header from "components/Headers/Header.js";
import DetectionChart from "components/AudioPlayer/DetectionChart";
import MonthlyDetectionChart from "components/AudioPlayer/MonthlyDetectionChart";

const API_BASE = process.env.REACT_APP_API_URL || "/api";
const EARLIEST_DATE = moment("2025-05-23");
const TODAY = moment();

const HistoryView = () => {
  const { historydate } = useParams();
  const navigate = useNavigate();

  const parsedDate = historydate && moment(historydate, "YYYY-MM-DD").isValid()
    ? moment(historydate, "YYYY-MM-DD")
    : TODAY;

  const [selectedDate, setSelectedDate] = useState(parsedDate.toDate());
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isoDate = moment(selectedDate).format("YYYY-MM-DD");
    navigate(`/admin/history/${isoDate}`);

    fetch(`${API_BASE}/detections/history?date=${isoDate}`)
      .then((res) => res.json())
      .then((data) => setHistoryData(data))
      .catch((err) => console.error("Error fetching history data:", err))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  if (loading) return <Container><Spinner color="primary" /></Container>;
  if (!historyData) return <Container><p>Error loading history data.</p></Container>;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col md="12">
            <Card className="shadow">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={{ before: new Date('2025-05-23'), after: new Date() }}
                />
              </CardHeader>
              <CardBody>
                <div style={{ marginBottom: "24px", paddingBottom: "24px", borderBottom: "1px solid #e9ecef" }}>
                  <MonthlyDetectionChart
                    year={moment(selectedDate).year()}
                    month={moment(selectedDate).month() + 1}
                  />
                </div>
                <div>
                  <h6 style={{ marginBottom: "12px", color: "#32325d", fontWeight: "600" }}>
                    {moment(selectedDate).format("MMMM D, YYYY")}
                  </h6>
                  <p><strong>Total Detections:</strong> {historyData.totalDetections}</p>
                  <p><strong>Unique Species:</strong> {historyData.speciesCount}</p>
                  {historyData.topDetection && (
                    <p><strong>Top Detection:</strong> {historyData.topDetection.common} ({historyData.topDetection.confidence} @ {historyData.topDetection.time})</p>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md="12">
            <Card className="shadow">
              <CardHeader><h4>Species Detected</h4></CardHeader>
              <CardBody>
                <Table responsive>
                  <thead className="thead-light">
                    <tr>
                      <th>Common Name</th>
                      <th>Scientific Name</th>
                      <th>Highest Confidence</th>
                      <th>First Detected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.speciesList.map((s, i) => (
                      <tr key={i}>
                        <td>
                          <a href={`/admin/species/${encodeURIComponent(s.common)}`}>{s.common}</a>
                        </td>
                        <td><i>{s.scientific}</i></td>
                        <td>{s.confidence}</td>
                        <td>{s.firstDetected}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md="12">
            <DetectionChart date={moment(selectedDate).format("YYYY-MM-DD")} />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default HistoryView;
